import { type FunctionComponent, useEffect } from "react";

import { BTLocalStorage } from "types/btStorage";

import { hasCredentialModalRendered } from "entity/login/credentialUpgradeState";
import { handleLogout } from "entity/login/Login/Login.utils";

/**
 * Component that checks if a credential upgrade is required before allowing app entry.
 * Uses a two-part system to distinguish between initial login and page refresh:
 *
 * - localStorage flag: Persists the credential upgrade requirement across page reloads
 * - Module-level variable: Set in Login.utils when upgrade flow begins (resets to false on page refresh)
 *
 * Only logs out if:
 * - localStorage flag exists (credential upgrade is pending)
 * - Module variable is false (indicates page refresh attempting to bypass)
 * - Flag hasn't expired (within 24 hours)
 *
 * This prevents logout during the initial login where the modal should show,
 * while catching users who refresh the page to bypass the requirement.
 *
 * The localStorage flag is automatically cleared when:
 * - User completes credential upgrade successfully
 * - Any user logs in successfully without needing credential upgrade
 * - 24 hours pass (expiration)
 *
 * Cross-tab synchronization:
 * - Listens for storage events from other tabs
 * - If another tab refreshes during upgrade, other tabs will also log out
 */
const RedirectForCredentialUpgrade: FunctionComponent = () => {
    useEffect(() => {
        const checkCredentialUpgradeFlag = () => {
            const upgradeInfo = BTLocalStorage.get("bt-object-credentialUpgradeRequired");

            // Only proceed if localStorage flag exists
            if (!upgradeInfo) {
                return;
            }

            // Check if flag has expired
            if (Date.now() > upgradeInfo.expiresAt) {
                // Flag expired, clear it and allow app entry
                BTLocalStorage.remove("bt-object-credentialUpgradeRequired");
                return;
            }

            // If credential upgrade flow is active (set in Login.utils before components mount),
            // this is the initial login - don't log out, let the user interact with the modal
            // On page refresh, the module-level variable resets to false
            if (hasCredentialModalRendered()) {
                return;
            }

            // localStorage flag exists, upgrade flow not active, and flag hasn't expired
            // This is a page refresh or new tab attempting to bypass - log them out
            handleLogout();
        };

        // Check on mount
        checkCredentialUpgradeFlag();

        // Listen for storage changes from other tabs
        // This ensures that if Tab 1 refreshes during upgrade and sets the flag,
        // Tab 2 will also log out automatically
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === "bt-object-credentialUpgradeRequired" && event.newValue) {
                checkCredentialUpgradeFlag();
            }
        };

        window.addEventListener("storage", handleStorageChange);

        return () => {
            window.removeEventListener("storage", handleStorageChange);
        };
    }, []);

    // Return null as handleLogout will handle the redirect
    return null;
};

export default RedirectForCredentialUpgrade;
